import { PERFORMANCES_NAME, RELATIONSHIP_SCORE_NAME } from './../model/score-card.model';
import * as d3 from 'd3';

export class Model {
    axis: string;
    value: number;
}

export class RadarChart {

    // initiate default config
    width = 400;
    height = 400;
    // add touch to mouseover and mouseout
    config = {
        w: this.width,
        h: this.height,
        facet: false,
        levels: 10,
        levelScale: 0.9,
        labelScale: 1.0,
        facetPaddingScale: 2.5,
        maxValue: 0,
        radians: 2 * Math.PI,
        polygonAreaOpacity: 0.3,
        polygonStrokeOpacity: 1,
        polygonPointSize: 4,
        legendBoxSize: 10,
        translateX: this.width / 4,
        translateY: this.height / 6,
        paddingX: this.width,
        paddingY: this.height,
        colors: d3.scaleOrdinal(d3.schemeCategory10),
        showLevels: true,
        showLevelsLabels: false,
        showAxesLabels: true,
        showAxes: true,
        showLegend: true,
        showVertices: true,
        showPolygons: true,
        showLabelToolTip: true
    };

    // initiate main vis component
    vis = {
        svg: null,
        tooltip: null,
        levels: null,
        axis: null,
        vertices: null,
        legend: null,
        allAxis: null,
        total: null,
        radius: null,
        totalAxes: null,
        verticesTooltip: [],
        axes: null
    };

    over = 'ontouchstart' in window ? 'touchstart' : 'mouseover';
    out = 'ontouchstart' in window ? 'touchend' : 'mouseout';
    draw(id, data) {


        // feed user configuration options
        if ('undefined' !== typeof this.config) {
            for (const i in this.config) {
                if ('undefined' !== typeof this.config[i]) {
                    this.config[i] = this.config[i];
                }
            }
        }

        this.render(data, id); // render the visualization
    }
    // render the visualization
    render(data, id) {
        // remove existing svg if exists
        d3.select(id).selectAll('svg').remove();
        this.updateConfig(data);

        if (this.config.facet) {
            data.forEach((d, i) => {
                this.buildVis([d], id); // build svg for each data group

                // override colors
                this.vis.svg.selectAll('.polygon-areas')
                    .attr('stroke', this.config.colors(i))
                    .attr('fill', this.config.colors(i));
                this.vis.svg.selectAll('.polygon-vertices')
                    .attr('fill', this.config.colors(i));
                this.vis.svg.selectAll('.legend-tiles')
                    .attr('fill', this.config.colors(i));
            });
        } else {
            this.buildVis(data, id); // build svg
        }
    }


    // update configuration parameters
    updateConfig(data) {
        // adjust config parameters
        const values = [];
        // data.forEach(d => values.push(...d.map(e => e.value)));
        this.config.maxValue = 100;
        this.config.w *= this.config.levelScale;
        this.config.h *= this.config.levelScale;
        this.config.paddingX = this.config.w * (this.config.levelScale) + 100;
        this.config.paddingY = this.config.h * (this.config.levelScale);


        // if facet required:
        if (this.config.facet) {
            this.config.w /= data.length;
            this.config.h /= data.length;
            this.config.paddingX /= (data.length / this.config.facetPaddingScale);
            this.config.paddingY /= (data.length / this.config.facetPaddingScale);
            this.config.polygonPointSize *= Math.pow(0.9, data.length);
        }
    }


    // build this.visualization using the other build helper functions
    buildVis(data, id) {
        this.buildVisComponents(data, id);
        this.buildCoordinates(data);
        if (this.config.showLevels) { this.buildLevels(); }
        if (this.config.showLevelsLabels) { this.buildLevelsLabels(); }
        if (this.config.showAxes) { this.buildAxes(); }
        if (this.config.showAxesLabels) { this.buildAxesLabels(); }
        if (this.config.showVertices) { this.buildVertices(data); }
        if (this.config.showPolygons) { this.buildPolygons(data); }
        this.buildTooltipLabel(data[0]);
    }

    // build main vis components
    buildVisComponents(data, id) {
        // update vis parameters
        this.vis.allAxis = data[0].map((i, j) => i.axis);
        this.vis.totalAxes = this.vis.allAxis.length;
        this.vis.radius = Math.min(this.config.w / 2, this.config.h / 2);

        // create main this.vis svg
        this.vis.svg = d3.select(id)
            .append('svg').classed('svg-vis', true)
            .attr('width', this.config.w + this.config.paddingX / 2)
            .attr('height', this.config.h + this.config.paddingY / 2)
            .append('svg:g')
            .attr('transform', 'translate(' + this.config.translateX + ',' + this.config.translateY + ')');


        // create levels
        this.vis.levels = this.vis.svg.selectAll('.levels')
            .append('svg:g').classed('levels', true);

        // create axes
        this.vis.axes = this.vis.svg.selectAll('.axes')
            .append('svg:g').classed('axes', true);

        // create vertices
        this.vis.vertices = this.vis.svg.selectAll('.vertices');

        // Initiate Legend
        this.vis.legend = this.vis.svg.append('svg:g').classed('legend', true)
            .attr('height', this.config.h / 2)
            .attr('width', this.config.w / 2)
            .attr('transform', 'translate(' + 0 + ', ' + 1.1 * this.config.h + ')');
    }


    // builds out the levels of the spiderweb
    buildLevels() {
        for (let level = 0; level < this.config.levels; level++) {
            const levelFactor = this.vis.radius * ((level + 1) / this.config.levels);

            // build level-lines
            this.vis.levels
                .data(this.vis.allAxis).enter()
                .append('svg:line').classed('level-lines', true)
                .attr('x1', (d, i) => levelFactor * (1 - Math.sin(i * this.config.radians / this.vis.totalAxes)))
                .attr('y1', (d, i) => levelFactor * (1 - Math.cos(i * this.config.radians / this.vis.totalAxes)))
                .attr('x2', (d, i) => levelFactor * (1 - Math.sin((i + 1) * this.config.radians / this.vis.totalAxes)))
                .attr('y2', (d, i) => levelFactor * (1 - Math.cos((i + 1) * this.config.radians / this.vis.totalAxes)))
                .attr('transform', 'translate(' + (this.config.w / 2 - levelFactor) + ', ' + (this.config.h / 2 - levelFactor) + ')')
                .attr('stroke', 'gray')
                .attr('stroke-width', '0.5px');
        }
    }


    // builds out the levels labels
    buildLevelsLabels() {
        for (let level = 0; level < this.config.levels; level++) {
            const levelFactor = this.vis.radius * ((level + 1) / this.config.levels);

            // build level-labels
            this.vis.levels
                .data([1]).enter()
                .append('svg:text').classed('level-labels', true)
                .text((this.config.maxValue * (level + 1) / this.config.levels).toFixed(2))
                .attr('x', (d) => levelFactor * (1 - Math.sin(0)))
                .attr('y', (d) => levelFactor * (1 - Math.cos(0)))
                .attr('transform', 'translate(' + (this.config.w / 2 - levelFactor + 5) + ', ' + (this.config.h / 2 - levelFactor) + ')')
                .attr('fill', 'gray')
                .attr('font-family', 'sans-serif')
                .attr('font-size', 10 * this.config.labelScale + 'px');
        }
    }


    // builds out the axes
    buildAxes() {
        this.vis.axes
            .data(this.vis.allAxis).enter()
            .append('svg:line').classed('axis-lines', true)
            .attr('x1', this.config.w / 2)
            .attr('y1', this.config.h / 2)
            .attr('x2', (d, i) => this.config.w / 2 * (1 - Math.sin(i * this.config.radians / this.vis.totalAxes)))
            .attr('y2', (d, i) => this.config.h / 2 * (1 - Math.cos(i * this.config.radians / this.vis.totalAxes)))
            .attr('stroke', 'grey')
            .attr('stroke-width', '1px');
    }


    // builds out the axes labels
    buildAxesLabels() {
        this.vis.axes
            .data(this.vis.allAxis).enter()
            .append('svg:text').classed('axis-labels', true)
            .text((d) => RELATIONSHIP_SCORE_NAME[d] ? RELATIONSHIP_SCORE_NAME[d] : PERFORMANCES_NAME[d])
            .attr('text-anchor', 'middle')
            .attr('x', (d, i) => this.config.w / 2 * (1 - 1.2 * Math.sin(i * this.config.radians / this.vis.totalAxes)))
            .attr('y', (d, i) => this.config.h / 2 * (1 - 1.1 * Math.cos(i * this.config.radians / this.vis.totalAxes)))
            .attr('font-family', 'sans-serif')
            .attr('font-size', 11 * this.config.labelScale + 'px');
    }

    buildTooltipLabel(data) {
        this.vis.axes
            .data(data).enter()
            .append('svg:text').classed('tooltip-axes', true)
            .text((d) => d.label)
            .attr('text-anchor', 'middle')
            .attr('x', (d, i) => this.config.w / 2 * (1 - 1.2 * Math.sin(i * this.config.radians / this.vis.totalAxes)))
            .attr('y', (d, i) => this.config.h / 2 * (1 - 1.1 * Math.cos(i * this.config.radians / this.vis.totalAxes)) + 30)
            .attr('font-family', 'sans-serif')
            .attr('font-size', 11 * this.config.labelScale + 'px')
            .attr('opacity', 1)
            .attr('z-index', 9999);
        // .call(this.getBB(data));

        this.vis.axes
            .data(data)
            .enter()
            .insert('rect', 'text')
            .attr('x', (d, i) => this.config.w / 2 * (1 - 1.2 * Math.sin(i * this.config.radians / this.vis.totalAxes)) - 40)
            .attr('y', (d, i) => this.config.h / 2 * (1 - 1.1 * Math.cos(i * this.config.radians / this.vis.totalAxes)) + 10)
            .attr('width', 80)
            .attr('height', 30)
            .attr('opacity', 0.5)
            .attr('fill', (d) => this.setBacgroundColor(d.color));

    }

    // builds [x, y] coordinates of polygon vertices.
    buildCoordinates(data) {
        data.forEach((group) => {
            group.map((d, i) => {
                d.axes = {
                    x: this.config.w / 2 * (1 - (parseFloat(Math.max(d.value, 0).toString()) /
                        this.config.maxValue) * Math.sin(i * this.config.radians / this.vis.totalAxes)),
                    y: this.config.h / 2 * (1 - (parseFloat(Math.max(d.value, 0).toString()) /
                        this.config.maxValue) * Math.cos(i * this.config.radians / this.vis.totalAxes))
                };
                return d.axes;
            });
        });
    }


    // builds out the polygon vertices of the dataset
    buildVertices(data) {
        data.forEach((group, g) => {
            let x = 0;
            let y = 0;
            this.vis.vertices
                .data(group).enter()
                .append('svg:circle').classed('polygon-vertices', true)
                .attr('r', this.config.polygonPointSize)
                .attr('cx', (d, i) => { x = d.axes.x; return d.axes.x; })
                .attr('cy', (d, i) => { y = d.axes.y; return d.axes.y; })
                .attr('id', (d, i) => 'polygon-vertices-' + i)
                .style('position', 'relative')
                .attr('fill', this.config.colors(g))

        });
    }


    // builds out the polygon areas of the dataset
    buildPolygons(data) {
        this.vis.vertices
            .data(data).enter()
            .append('svg:polygon').classed('polygon-areas', true)
            .attr('points', (group) => { // build verticesString for each group
                let verticesString = '';
                group.forEach((d, i) => {
                    const width = this.config.w / 2 * (1 - 1.1 * Math.sin(i * this.config.radians / this.vis.totalAxes));
                    const height = this.config.h / 2 * (1 - 1.1 * Math.cos(i * this.config.radians / this.vis.totalAxes));
                    verticesString += d.axes.x + ',' + d.axes.y + ' ';
                });
                return verticesString;
            })
            .attr('stroke-width', '2px')
            .attr('stroke', (d, i) => this.config.colors(i))
            .attr('fill', (d, i) => this.config.colors(i))
            .attr('fill-opacity', 0.3)
            .attr('stroke-opacity', this.config.polygonStrokeOpacity)
            .on(this.over, (d) => {
                this.vis.svg.selectAll('.polygon-areas') // fade all other polygons out
                    .transition(250)
                    .attr('fill-opacity', 0.5)
                    .attr('stroke-opacity', this.config.polygonStrokeOpacity);
                d3.select('polygon-vertices') // focus on active polygon
                    .transition('250')
                    .attr('fill-opacity', 0)
                    .attr('stroke-opacity', this.config.polygonStrokeOpacity);
            })
            .on(this.out, () => {
                d3.selectAll('.polygon-areas')
                    .transition('250')
                    .attr('fill-opacity', 0.3)
                    .attr('stroke-opacity', 1);
            });
    }


    // builds out the legend
    buildLegend(data) {
        // Create legend squares
        // this.vis.legend.selectAll('.legend-tiles')
        //     .data(data).enter()
        //     .append('svg:rect').classed('legend-tiles', true)
        //     .attr('x', this.config.w - this.config.paddingX / 2)
        //     .attr('y', (d, i) => i * 2 * this.config.legendBoxSize)
        //     .attr('width', this.config.legendBoxSize)
        //     .attr('height', this.config.legendBoxSize)
        //     .attr('fill', (d, g) => this.config.colors(g));

        // Create text next to squares
        this.vis.legend.selectAll('.legend-labels')
            .data(data).enter()
            .append('svg:text').classed('legend-labels', true)
            .attr('x', this.config.w - this.config.paddingX / 2 + (1.5 * this.config.legendBoxSize))
            .attr('y', (d, i) => i * 2 * this.config.legendBoxSize)
            .attr('dy', 0.07 * this.config.legendBoxSize + 'em')
            .attr('font-size', 11 * this.config.labelScale + 'px')
            .attr('fill', 'gray')
            .text((d) => {
                return d.group;
            });
    }

    setBacgroundColor(value) {
        if (value === 'red') {
            return '#e15759';
        } else if (value === 'green') {
            return '#59a14f';
        }
        return '#f28e2b';
    }
}
